// The Web Audio wrapper (plan 18). Three layers so the parts that carry
// logic run in vitest without a browser:
//
//   AudioEngine        — mute flags + the two-channel jukebox command model,
//                        over an injected `AudioBackend` (pure bookkeeping)
//   createWebAudioBackend — the only Web Audio code: lazy AudioContext,
//                        autoplay unlock, fetch/decode cache, voices
//
// The command model is the original `JukeBox`'s: a play command on a channel
// replaces what that channel plays; channel 0 is the music, channel 1 the
// level-up stinger overlay. Two deliberate simplifications of its 0.1 s
// poll loop (DIVERGENCES.md): looping uses native `loop = true` instead of
// re-triggering on the next poll (the original's ~0.1 s gap per lap is an
// artefact), and `fadeOut` is a 0.7 s linear gain ramp instead of the
// −0.1-per-0.1 s staircase (0.7 s is what the staircase takes from the
// standard 0.7 music volume).

import type { MusicTrack } from "./banks";

/** The ramp `fadeOutMusic` runs before freeing the channel. */
export const MUSIC_FADE_SECONDS = 0.7;

/** A started music voice. */
export interface VoiceHandle {
  stop(): void;
  /** Ramp to silence over `seconds`, then stop. */
  fadeOut(seconds: number): void;
}

/**
 * What the engine needs from the platform. The real one is
 * `createWebAudioBackend`; tests inject a recorder.
 */
export interface AudioBackend {
  /** True until the first user gesture has resumed the AudioContext. */
  readonly locked: boolean;
  /**
   * Call from a genuine user-gesture handler. Idempotent; `onUnlocked`
   * callbacks fire once, after the context actually resumed.
   */
  unlock(onUnlocked: () => void): void;
  /** Fire-and-forget one-shot at full volume, optionally delayed. */
  playOnce(url: string, delaySeconds: number): void;
  /**
   * Start a music voice. `onEnded` fires when a non-looping voice plays out
   * naturally (never after `stop`/`fadeOut`). Returns null when the voice
   * cannot start (no context yet); the caller keeps the command and retries
   * on unlock.
   */
  startVoice(url: string, volume: number, loop: boolean, onEnded: () => void): VoiceHandle | null;
}

interface Channel {
  track: MusicTrack;
  handle: VoiceHandle | null;
}

/**
 * Mute flags and the jukebox command model. `setSoundOn`/`setMusicOn` gate at
 * **call time** — deliberately not the original's `MusicIsOn`-at-scene-load
 * permanence, where toggling music back on did nothing until the next scene
 * load because the poll coroutines never started (DIVERGENCES.md →
 * Corrections). Turning music off stops the voices but keeps the commands, so
 * turning it back on resumes what should be playing; same trick covers the
 * autoplay unlock (commands issued while locked start at the gesture).
 */
export class AudioEngine {
  private soundOn = true;
  private musicOn = true;
  private readonly channels = new Map<0 | 1, Channel>();

  constructor(private readonly backend: AudioBackend) {}

  /** Forward a user gesture to the backend; starts any pending music. */
  unlock(): void {
    this.backend.unlock(() => this.startPendingChannels());
  }

  get locked(): boolean {
    return this.backend.locked;
  }

  /** One-shot SFX; skipped (not queued) while muted or locked. */
  playSfx(url: string, delaySeconds = 0): void {
    if (!this.soundOn || this.backend.locked) return;
    this.backend.playOnce(url, delaySeconds);
  }

  /** Issue a jukebox play command: replaces what the track's channel plays. */
  playMusic(track: MusicTrack): void {
    const previous = this.channels.get(track.channel);
    previous?.handle?.stop();
    const channel: Channel = { track, handle: null };
    this.channels.set(track.channel, channel);
    this.startChannel(channel);
  }

  /** Fade the channel out and free it (`JukeBox.FadeOutJukeBox`). */
  fadeOutMusic(channelNumber: 0 | 1): void {
    const channel = this.channels.get(channelNumber);
    if (!channel) return;
    channel.handle?.fadeOut(MUSIC_FADE_SECONDS);
    this.channels.delete(channelNumber);
  }

  /** The command a channel currently holds — for tests and debugging. */
  currentTrack(channelNumber: 0 | 1): MusicTrack | null {
    return this.channels.get(channelNumber)?.track ?? null;
  }

  setSoundOn(on: boolean): void {
    this.soundOn = on;
  }

  setMusicOn(on: boolean): void {
    this.musicOn = on;
    if (on) {
      this.startPendingChannels();
      return;
    }
    for (const channel of this.channels.values()) {
      channel.handle?.stop();
      channel.handle = null;
    }
  }

  private startChannel(channel: Channel): void {
    if (!this.musicOn || this.backend.locked) return;
    const { track } = channel;
    channel.handle = this.backend.startVoice(track.url, track.volume, track.loop, () => {
      // A one-shot that played out frees its channel, so a later unlock or
      // music-on toggle does not restart a finished track from the top.
      if (this.channels.get(track.channel) === channel) this.channels.delete(track.channel);
    });
  }

  private startPendingChannels(): void {
    for (const channel of this.channels.values()) {
      if (channel.handle === null) this.startChannel(channel);
    }
  }
}

/**
 * The real backend. One lazy `AudioContext` — created on the first `unlock`
 * gesture, so the page never trips the autoplay policy or its console
 * warning — with a `master -> sfx/music` gain graph. Fetches are warmable
 * ahead of the context (bytes only); decoding waits for the context and is
 * cached per URL. A fetch or decode failure logs once and disables that clip
 * — audio must never take the game down.
 */
export function createWebAudioBackend(): AudioBackend & { warm(urls: readonly string[]): void } {
  let ctx: AudioContext | null = null;
  let sfxGain: GainNode | null = null;
  let musicGain: GainNode | null = null;
  let locked = true;
  const unlockedCallbacks: (() => void)[] = [];
  // url -> raw bytes promise (pre-context), then decoded buffer (or null =
  // failed, logged once).
  const bytes = new Map<string, Promise<ArrayBuffer | null>>();
  const buffers = new Map<string, Promise<AudioBuffer | null>>();

  const fetchBytes = (url: string): Promise<ArrayBuffer | null> => {
    let pending = bytes.get(url);
    if (!pending) {
      pending = fetch(url)
        .then((res) => {
          if (!res.ok) throw new Error(`HTTP ${res.status}`);
          return res.arrayBuffer();
        })
        .catch((err: unknown) => {
          console.warn(`audio: failed to fetch ${url}:`, err);
          return null;
        });
      bytes.set(url, pending);
    }
    return pending;
  };

  const decode = (url: string): Promise<AudioBuffer | null> => {
    let pending = buffers.get(url);
    if (!pending) {
      const context = ctx;
      if (!context) return Promise.resolve(null);
      pending = fetchBytes(url).then((raw) => {
        if (raw === null) return null;
        // decodeAudioData detaches its input; copy so a retry stays possible.
        return context.decodeAudioData(raw.slice(0)).catch((err: unknown) => {
          console.warn(`audio: failed to decode ${url}:`, err);
          return null;
        });
      });
      buffers.set(url, pending);
    }
    return pending;
  };

  return {
    get locked() {
      return locked;
    },

    warm(urls: readonly string[]): void {
      for (const url of urls) void fetchBytes(url);
    },

    unlock(onUnlocked: () => void): void {
      if (!locked) return;
      unlockedCallbacks.push(onUnlocked);
      if (!ctx) {
        ctx = new AudioContext();
        const master = ctx.createGain();
        master.connect(ctx.destination);
        sfxGain = ctx.createGain();
        sfxGain.connect(master);
        musicGain = ctx.createGain();
        musicGain.connect(master);
      }
      void ctx.resume().then(() => {
        if (!locked) return;
        locked = false;
        for (const cb of unlockedCallbacks.splice(0)) cb();
      });
    },

    playOnce(url: string, delaySeconds: number): void {
      const context = ctx;
      const out = sfxGain;
      if (!context || !out) return;
      const at = context.currentTime + delaySeconds;
      void decode(url).then((buffer) => {
        if (!buffer) return;
        const source = context.createBufferSource();
        source.buffer = buffer;
        source.connect(out);
        // A delay that decode time already ate plays immediately (start
        // clamps past times to "now"), which is the right degradation.
        source.start(at);
      });
    },

    startVoice(url: string, volume: number, loop: boolean, onEnded: () => void): VoiceHandle | null {
      const context = ctx;
      const out = musicGain;
      if (!context || !out) return null;
      const gain = context.createGain();
      gain.gain.value = volume;
      gain.connect(out);
      let source: AudioBufferSourceNode | null = null;
      let cancelled = false;
      let fading = false;
      void decode(url).then((buffer) => {
        if (!buffer || cancelled) return;
        source = context.createBufferSource();
        source.buffer = buffer;
        source.loop = loop;
        source.connect(gain);
        source.onended = () => {
          gain.disconnect();
          if (!cancelled && !fading) onEnded();
        };
        source.start();
      });
      return {
        stop(): void {
          cancelled = true;
          source?.stop();
          gain.disconnect();
        },
        fadeOut(seconds: number): void {
          if (cancelled || fading) return;
          fading = true;
          const now = context.currentTime;
          gain.gain.setValueAtTime(gain.gain.value, now);
          gain.gain.linearRampToValueAtTime(0, now + seconds);
          if (source) {
            source.stop(now + seconds);
          } else {
            // Still decoding: nothing audible yet, just never start it.
            cancelled = true;
          }
        },
      };
    },
  };
}
